(() => {
  const root = window.Shopify?.routes?.root || '/';
  let returnFocus = null;

  const getShell = () => document.querySelector('[data-cart-drawer-shell]');
  const getDrawer = () => document.querySelector('[data-cart-drawer]');
  const getStatus = () => document.querySelector('[data-cart-drawer-status]');

  const setCartCount = (count) => {
    document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
      let badge = trigger.querySelector('.cart-count');
      if (count > 0 && !badge) {
        badge = document.createElement('span');
        badge.className = 'cart-count';
        trigger.append(badge);
      }
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count < 1;
      }
      trigger.setAttribute('aria-label', count > 0 ? `Open cart with ${count} items` : 'Open cart');
    });
  };

  const setBusy = (busy, message = '') => {
    getDrawer()?.toggleAttribute('data-loading', busy);
    const status = getStatus();
    if (status && message) status.textContent = message;
  };

  const openDrawer = (rememberFocus = true) => {
    const drawer = getDrawer();
    if (!drawer) return;
    document.querySelector('[data-menu-close]')?.click();
    document.querySelector('[data-search-close]')?.click();
    if (rememberFocus) returnFocus = document.activeElement;
    drawer.setAttribute('data-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.inert = false;
    getShell()?.setAttribute('data-open', '');
    document.body.classList.add('cart-drawer-open');
    document.querySelectorAll('[data-cart-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    window.setTimeout(() => drawer.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true }), 80);
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    drawer?.removeAttribute('data-open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (drawer) drawer.inert = true;
    getShell()?.removeAttribute('data-open');
    document.body.classList.remove('cart-drawer-open');
    document.querySelectorAll('[data-cart-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (returnFocus?.focus) returnFocus.focus({ preventScroll: true });
  };

  const refresh = async ({ open = false, message = '' } = {}) => {
    setBusy(true, message || 'Updating your cart…');
    try {
      const cartUrl = new URL(`${root}cart`, window.location.origin);
      cartUrl.searchParams.set('_drawer', Date.now().toString());
      cartUrl.searchParams.set('section_id', 'cart-drawer-content');
      const [cartResponse, htmlResponse] = await Promise.all([
        fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
        fetch(cartUrl.toString(), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      ]);
      if (!cartResponse.ok || !htmlResponse.ok) throw new Error('Cart could not be refreshed.');
      const cart = await cartResponse.json();
      const parsed = new DOMParser().parseFromString(await htmlResponse.text(), 'text/html');
      const replacement = parsed.querySelector('[data-cart-drawer-shell]');
      const current = getShell();
      if (!replacement || !current) throw new Error('Your cart was updated. Please open the cart page to review it.');
      current.replaceWith(replacement);
      setCartCount(Number(cart.item_count || 0));
      if (open) openDrawer(false);
      const status = getStatus();
      if (status && message) status.textContent = message;
      return cart;
    } catch (error) {
      setBusy(false, error.message || 'Cart could not be updated. Please try again.');
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const addForm = async (form, { message = 'Added to your tackle box.' } = {}) => {
    setBusy(true, 'Adding item…');
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: new FormData(form)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');
    return refresh({ open: true, message });
  };

  const changeLine = async (key, quantity) => {
    setBusy(true, 'Updating quantity…');
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Quantity could not be updated.');
    return refresh({ open: true, message: quantity > 0 ? 'Quantity updated.' : 'Item removed.' });
  };

  document.addEventListener('click', async (event) => {
    const openTrigger = event.target.closest?.('[data-cart-open]');
    if (openTrigger && getDrawer()) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (event.target.closest?.('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const quantityButton = event.target.closest?.('[data-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      if (getDrawer()?.hasAttribute('data-loading')) return;
      const current = Number(quantityButton.dataset.quantity || 0);
      const quantity = quantityButton.dataset.cartQuantity === 'increase' ? current + 1 : current - 1;
      try { await changeLine(quantityButton.dataset.lineKey, quantity); } catch (error) { setBusy(false, error.message); }
      return;
    }
    const removeButton = event.target.closest?.('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      if (getDrawer()?.hasAttribute('data-loading')) return;
      try { await changeLine(removeButton.dataset.lineKey, 0); } catch (error) { setBusy(false, error.message); }
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-cart-upsell-form]')) {
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      if (button?.disabled) return;
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      try { await addForm(form); } catch (error) { setBusy(false, error.message); if (button) { button.disabled = false; button.textContent = 'Try again'; } }
      return;
    }

    if (form.matches('[data-cart-note-form]')) {
      event.preventDefault();
      const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin', body: new FormData(form) });
      if (response.ok) await refresh({ open: true, message: 'Order note saved.' });
      else setBusy(false, 'Order note could not be saved.');
      return;
    }

    if (form.matches('.product-form') && !event.defaultPrevented) {
      if (event.submitter?.closest?.('.shopify-payment-button')) return;
      event.preventDefault();
      const button = form.querySelector('[data-product-submit]');
      const label = form.querySelector('[data-product-submit-label]');
      const errorMessage = form.querySelector('[data-product-form-error]');
      if (button?.disabled) return;
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      if (label) label.textContent = 'Adding…';
      if (errorMessage) errorMessage.hidden = true;
      try {
        await addForm(form);
        if (label) label.textContent = '✓ Added';
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        window.setTimeout(() => { if (label) label.textContent = 'Add to cart'; }, 1500);
      } catch (error) {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        if (label) label.textContent = 'Try again';
        if (errorMessage) { errorMessage.textContent = error.message; errorMessage.hidden = false; }
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && getDrawer()?.hasAttribute('data-open')) closeDrawer();
  });

  window.FishingLureCart = { open: openDrawer, close: closeDrawer, refresh };
  if (getDrawer()) getDrawer().inert = true;
})();
